import { Proposal, ProposalAgent } from '@/services/proposals/types';
import { resolveInsuranceTypeLabel } from '@/shared/enums/insurance-type.enum';
import { toaster } from '@/shared/ui/toaster.component';
import { formatCurrency, formatDateToBrazil } from '@/shared/utils';
import { formatPhone } from '@/shared/utils/format-phone';
import {
  formatRegisterNumber,
  isRegisterNumberCnpjMask,
} from '@/shared/utils/format-register-number';
import { formatZipcode } from '@/shared/utils/format-zipcode.util';

import { Button, Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  FilePlus2,
  FileText,
  Folder,
  SquarePen,
  TrendingUp,
} from 'lucide-react';
import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';

import { proposalsService } from '@/services/proposals';
import { useSelectedBrokerWriteGuard } from '../../../brokers/hooks';
import { ManualEmissionModal } from '../../../integrations/components/manual-emission-modal.component';
import { useManualEmissionAction } from '../../../integrations/hooks/use-manual-emission-action.hook';
import { InspectionAttachmentsModal } from '../../../inspections/components';
import { useProposalInspectionDocument } from '../../hooks';
import { useInspectionHistory } from '../../../inspections/hooks/use-inspection-history.hook';

import {
  PORTAL_STATUS_LABELS,
  PortalStatus,
} from '../../../../shared/enums/portal-status.enum';
import { getQuoteStatus } from '@/shared/utils/get-quote-status..util';
import {
  CommercializationSection,
  InfoRow,
} from '../../../quotes/components/quote-details-sections.component';
import { type BusinessLocation } from '../../../quotes/components/quote-steps/business-location';
import { BusinessLocationCard } from '../../../quotes/components/quote-steps/business-location-card.component';
import { useConstructionTypes } from '../../../quotes/hooks/homeowner/use-construction-types.hook';
import { usePropertyClassifications } from '../../../quotes/hooks/homeowner/use-property-classifications.hook';
import { usePropertyTypes } from '../../../quotes/hooks/homeowner/use-property-types.hook';
import { mapBusinessRisksToLocations } from '../../../quotes/utils/map-business-risks-to-locations.util';
import type { BusinessRisk } from '@/services/quotes/types';
import type { ProductType } from '@/services';
import { usePaymentTypes } from '../../hooks';
import {
  isProposalSentToAnalysisError,
  useTransmitProposal,
} from '../../hooks/use-transmit-proposal.hook';
import {
  buildPaymentStateFromProposal,
  getPremiumInstallmentSummary,
} from './step-payment.component';
import { ModalConfirmTransmit } from './modal-confirm-transmit.component';
import { ModalProposalInAnalysis } from './modal-proposal-in-analysis.component';
import { ModalGeneratePDF } from './modal-generate-pdf.component';
import { ModalSendMail } from './modal-send-mail.component';
import { PremiumCard } from './premium-card.component';

type Props = {
  proposal: Proposal;
  isViewMode: boolean;
  isBusinessProposal?: boolean;
  /** Produto cujo catálogo resolve o rótulo da forma de pagamento escolhida. */
  productType: ProductType;
  onResumeEdit?: () => void;
};

/**
 * `statusPortal` nos quais a proposta não pode mais ser transmitida — o botão
 * "Transmitir Proposta" não deve ser exibido (ex.: efetivada, transmitida,
 * em inspeção, emitida, expirada, em análise ou recusada).
 */
const TRANSMIT_HIDDEN_PORTAL_STATUSES = new Set<number>([
  7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 18, 19,
]);

export function StepResume({
  proposal,
  isViewMode,
  isBusinessProposal = false,
  productType,
  onResumeEdit,
}: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const navigate = useNavigate();
  const proposalWriteGuard = useSelectedBrokerWriteGuard('proposal');
  const proposalActionGuard = useSelectedBrokerWriteGuard('proposal', 'other');
  const transmitMutation = useTransmitProposal();

  const totalValue = formatCurrency(proposal?.grossPremium);
  const liquidValue =
    proposal?.rawPremium != null ? formatCurrency(proposal.rawPremium) : '-';
  const interestPercentage = '0,00%';

  const interestValue = useMemo(() => {
    return formatCurrency(proposal?.pisCofins ?? 0);
  }, [proposal?.pisCofins]);

  const iofValue = proposal?.iof;
  const product = isBusinessProposal ? 'Empresarial' : 'Residencial';
  const proposalNumber = proposal?.proposalNo;

  const portalStatus = Number(proposal?.statusPortal);
  const isTransmitted = portalStatus === PortalStatus.ProposalTransmitted;
  const isPendingInspection =
    portalStatus === PortalStatus.ProposalPendingInspection;
  const isInspectionApproved =
    portalStatus === PortalStatus.ProposalInspectionApproved;
  const isInspectionRejected =
    portalStatus === PortalStatus.ProposalInspectionRejected;
  const isPostTransmitResume =
    isTransmitted ||
    isPendingInspection ||
    isInspectionApproved ||
    isInspectionRejected;
  const canTransmit = !TRANSMIT_HIDDEN_PORTAL_STATUSES.has(portalStatus);
  const isPending = transmitMutation.isPending;

  /**
   * "Emitir manualmente" só aparece na proposta TRANSMITIDA de produto em modo
   * manual, para o perfil Administrador, e enquanto o controle de emissão não
   * tiver apólice vinculada. Todas essas condições são resolvidas no hook.
   */
  const manualEmission = useManualEmissionAction({
    policyId: proposal?.policyId,
    isEligible: isTransmitted,
  });

  async function handleConfirmTransmit() {
    try {
      const result = await transmitMutation.mutateAsync({
        policyId: proposal.policyId,
        productCode: proposal.productCode,
      });
      setIsModalOpen(false);
      if (result.sentToAnalysis || isBusinessProposal) {
        setIsAnalysisModalOpen(true);
        return;
      }
      navigate({ to: '/proposals' });
    } catch (error) {
      setIsModalOpen(false);
      if (isProposalSentToAnalysisError(error)) {
        setIsAnalysisModalOpen(true);
        return;
      }
      toaster.create({
        type: 'error',
        title: 'Erro ao transmitir proposta',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }

  const { data: propertyTypes } = usePropertyTypes();

  const propertyType =
    propertyTypes?.find(
      pt => String(pt.code) === proposal?.lobs?.[0]?.risks?.[0]?.propertyType
    )?.name || '';

  const { data: constructionTypes } = useConstructionTypes();

  const constructionType =
    constructionTypes?.find(
      ct =>
        String(ct.code) === proposal?.lobs?.[0]?.risks?.[0]?.constructionType
    )?.description || '';

  const { data: propertyClassifications } = usePropertyClassifications();

  const propertyClassification =
    propertyClassifications?.find(
      pc =>
        String(pc.code) ===
        proposal?.lobs?.[0]?.risks?.[0]?.propertyClassification
    )?.description || '';
  const { data: inspectionHistory } = useInspectionHistory(proposal.policyId, {
    enabled: isInspectionRejected || isInspectionApproved,
  });

  const decisionRecord = useMemo(
    () =>
      inspectionHistory?.find(
        record =>
          record.action?.includes('Recusada') ||
          record.action?.includes('Aprovada')
      ),
    [inspectionHistory]
  );

  const inspectionId = decisionRecord?.inspectionId ?? '';

  const improvementLetterText = useMemo(() => {
    if (!inspectionHistory) return undefined;
    for (let i = inspectionHistory.length - 1; i >= 0; i--) {
      if (inspectionHistory[i].improvementLetter) {
        return inspectionHistory[i].improvementLetter;
      }
    }
    return undefined;
  }, [inspectionHistory]);

  const proponentRegisterNumber = proposal?.customer?.cpf || '';
  const isLegalPerson =
    proposal?.customer?.documentType === '2' ||
    proposal?.customer?.isOrgParty === 'Y' ||
    isRegisterNumberCnpjMask(proponentRegisterNumber);

  // ── Layout empresarial: mesmas seções do detalhes da cotação (Figma) ──
  const businessLocations = useMemo<BusinessLocation[]>(() => {
    if (!isBusinessProposal || !proposal?.lobs?.[0]?.risks?.length) return [];
    return mapBusinessRisksToLocations(
      proposal.lobs[0].risks as unknown as BusinessRisk[],
      { stableIds: true }
    );
  }, [isBusinessProposal, proposal]);

  const [expandedLocationIds, setExpandedLocationIds] = useState<Set<string>>(
    new Set()
  );
  const toggleLocation = (key: string) => {
    setExpandedLocationIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Pagamento: mesma hidratação e o MESMO catálogo do StepPayment — o rótulo
  // sai do produto da proposta, não de um tipo fixo.
  const paymentState = useMemo(
    () => (isBusinessProposal ? buildPaymentStateFromProposal(proposal) : null),
    [isBusinessProposal, proposal]
  );
  const installmentSummary = useMemo(
    () => getPremiumInstallmentSummary(proposal),
    [proposal]
  );
  const { data: paymentTypesData } = usePaymentTypes(
    isBusinessProposal ? productType : undefined
  );
  const paymentMethodLabel =
    paymentTypesData?.find(pt => pt.code === paymentState?.paymentMethod)
      ?.description ||
    paymentState?.paymentMethod ||
    '—';
  const installmentCount = Number(paymentState?.installment ?? 0);
  const installmentsLabel =
    installmentCount > 0
      ? installmentSummary.installmentValue > 0
        ? `${installmentCount}x de ${formatCurrency(installmentSummary.installmentValue)}`
        : `${installmentCount}x`
      : '—';

  return (
    <div className='flex flex-col gap-8'>
      <ManualEmissionModal
        isOpen={manualEmission.isOpen}
        onClose={manualEmission.close}
        onConfirm={manualEmission.confirm}
        isLoading={manualEmission.isPending}
        errorMessage={manualEmission.errorMessage}
        proposalNo={proposal?.proposalNo}
        requiresPolicyPdf={manualEmission.requiresPolicyPdf}
      />
      <ModalConfirmTransmit
        isOpen={isModalOpen}
        isLoading={isPending}
        isBusinessProposal={isBusinessProposal}
        requiresInspection={proposal.requiresInspection}
        onConfirm={handleConfirmTransmit}
        onClose={() => setIsModalOpen(false)}
      />
      <ModalProposalInAnalysis
        isOpen={isAnalysisModalOpen}
        onClose={() => {
          setIsAnalysisModalOpen(false);
          navigate({ to: '/proposals' });
        }}
      />
      <PremiumCard
        totalValue={totalValue}
        liquidValue={liquidValue}
        interestPercentage={interestPercentage}
        interestValue={interestValue}
        iofValue={formatCurrency(iofValue ?? 0)}
        product={product}
        proposalNumber={proposalNumber}
        status={
          PORTAL_STATUS_LABELS[portalStatus] ??
          getQuoteStatus(Number(proposal?.policyStatus ?? 1)).label
        }
        showPisCofins={false}
      />

      {isBusinessProposal ? (
        <>
          {/* Informações da Proposta — mesmo layout do detalhes da cotação */}
          <div className='flex flex-col gap-6'>
            <p className='font-montserrat text-base font-normal text-alba-black-500'>
              Informações da Proposta
            </p>

            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              {/* Dados da Emissão */}
              <div className='flex flex-col gap-4'>
                <p className='font-montserrat text-sm font-bold text-alba-black-500'>
                  Dados da Emissão
                </p>
                <div className='flex flex-col rounded-xl border border-alba-gray-400 px-4 py-2 divide-y divide-alba-gray-200'>
                  <InfoRow
                    label='Início de Vigência'
                    value={formatDateToBrazil(proposal?.effectiveDate)}
                  />
                  <InfoRow
                    label='Fim de Vigência'
                    value={formatDateToBrazil(proposal?.expiryDate)}
                  />
                  <InfoRow
                    label='Tipo de Seguro'
                    value={resolveInsuranceTypeLabel(proposal)}
                  />
                  <InfoRow
                    label='Registrado por'
                    value={proposal?.insertByUserName ?? ''}
                  />
                </div>
              </div>

              {/* Dados do Proponente */}
              <div className='flex flex-col gap-4'>
                <p className='font-montserrat text-sm font-bold text-alba-black-500'>
                  Dados do Proponente
                </p>
                <div className='flex flex-col rounded-xl border border-alba-gray-400 px-4 py-2 divide-y divide-alba-gray-200'>
                  <InfoRow
                    label={isLegalPerson ? 'CNPJ' : 'CPF'}
                    value={formatRegisterNumber(proponentRegisterNumber)}
                  />
                  <InfoRow
                    label={isLegalPerson ? 'Razão Social' : 'Nome'}
                    value={proposal?.customer?.name || ''}
                  />
                  <InfoRow
                    label='Telefone'
                    value={formatPhone(
                      proposal?.customer?.cellphone ||
                        proposal?.customer?.phone ||
                        ''
                    )}
                  />
                  <InfoRow
                    label='Email'
                    value={proposal?.customer?.email || ''}
                  />
                </div>
              </div>

              {/* Dados de Pagamento */}
              {paymentState && (
                <div className='flex flex-col gap-4'>
                  <p className='font-montserrat text-sm font-bold text-alba-black-500'>
                    Dados de Pagamento
                  </p>
                  <div className='flex flex-col rounded-xl border border-alba-gray-400 px-4 py-2 divide-y divide-alba-gray-200'>
                    <InfoRow
                      label='Forma de Pagamento'
                      value={paymentMethodLabel}
                    />
                    <InfoRow label='Parcelas' value={installmentsLabel} />
                    <InfoRow
                      label='1º Vencimento'
                      value={formatDateToBrazil(paymentState.firstDueDate)}
                    />
                    {paymentState.agency && (
                      <InfoRow
                        label='Agência'
                        value={
                          paymentState.agencyDigit
                            ? `${paymentState.agency}-${paymentState.agencyDigit}`
                            : paymentState.agency
                        }
                      />
                    )}
                    {paymentState.account && (
                      <InfoRow
                        label='Conta'
                        value={
                          paymentState.accountDigit
                            ? `${paymentState.account}-${paymentState.accountDigit}`
                            : paymentState.account
                        }
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lista de Locais */}
          {businessLocations.length > 0 && (
            <div className='flex flex-col gap-6'>
              <p className='font-montserrat text-base font-normal text-alba-black-500'>
                Lista de Locais
              </p>
              <div className='flex flex-col gap-4 w-full'>
                {businessLocations.map((location, index) => (
                  <BusinessLocationCard
                    key={location.id}
                    location={location}
                    locationNumber={index + 1}
                    isOpen={expandedLocationIds.has(location.id)}
                    canEdit={false}
                    onToggleLocation={() => toggleLocation(location.id)}
                    productId={
                      proposal?.productId != null
                        ? String(proposal.productId)
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* Comercialização */}
          <CommercializationSection agents={proposal?.agents ?? []} />
        </>
      ) : (
        <div className='flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-6'>
          <ResumeGroup title='Dados do Seguro'>
            <ResumeItem
              label='Início da Vigência'
              value={formatDateToBrazil(proposal?.effectiveDate)}
            />
            <ResumeItem
              label='Fim da Vigência'
              value={formatDateToBrazil(proposal?.expiryDate)}
            />
            <ResumeItem
              label='Tipo de Seguro'
              value={resolveInsuranceTypeLabel(proposal)}
            />
          </ResumeGroup>

          <ResumeGroup title='Dados do Proponente'>
            <ResumeItem
              label={isLegalPerson ? 'CNPJ' : 'CPF'}
              value={formatRegisterNumber(proponentRegisterNumber)}
            />
            <ResumeItem label='Nome' value={proposal?.customer?.name || ''} />
            {!isLegalPerson && (
              <ResumeItem
                label='Nome Social'
                value={proposal?.customer?.socialName || ''}
              />
            )}
            {!isLegalPerson ? (
              <>
                <ResumeItem
                  label='Sexo'
                  value={
                    proposal?.customer?.genderCode === 'M'
                      ? 'Masculino'
                      : proposal?.customer?.genderCode === 'F'
                        ? 'Feminino'
                        : ''
                  }
                />
                <ResumeItem
                  label='Data de Nascimento'
                  value={formatDateToBrazil(proposal?.customer?.dateOfBirth)}
                />
              </>
            ) : null}
            <ResumeItem
              label='Telefone'
              value={formatPhone(
                proposal?.customer?.cellphone || proposal?.customer?.phone || ''
              )}
            />
            <ResumeItem label='Email' value={proposal?.customer?.email || ''} />
          </ResumeGroup>

          <ResumeGroup title='Dados do Risco'>
            <ResumeItem
              label='CEP'
              value={formatZipcode(
                proposal?.lobs?.[0]?.risks?.[0]?.zipCode || ''
              )}
            />
            <ResumeItem label='Tipo do Imóvel' value={propertyType} />
            <ResumeItem label='Tipo de Construção' value={constructionType} />
            <ResumeItem
              label='Classificação do Imóvel'
              value={propertyClassification}
            />
          </ResumeGroup>

          <CommercializationResume agents={proposal?.agents ?? []} />
        </div>
      )}

      {(isInspectionRejected || isInspectionApproved) && (
        <DocumentationSection
          policyId={proposal.policyId}
          inspectionId={inspectionId}
          isRejected={isInspectionRejected}
          improvementLetterText={improvementLetterText}
          generatedAt={decisionRecord?.createdAt}
        />
      )}

      <div className='flex flex-col gap-2 self-stretch sm:flex-row sm:flex-wrap sm:items-center sm:justify-end'>
        {!isPostTransmitResume &&
          (!isViewMode || proposal.proposalStatus === '6') && (
            <Button
              type='button'
              variant='secondary'
              size='md'
              className='flex w-full items-center justify-center gap-2 rounded-xl sm:w-auto'
              onClick={() => {
                if (onResumeEdit) {
                  onResumeEdit();
                  return;
                }
                void navigate({
                  to: '/proposals/$id/edit',
                  params: { id: proposal.policyId },
                });
              }}
              disabled={isPending || proposalWriteGuard.blocked}
              title={proposalWriteGuard.reason}
            >
              <SquarePen className='size-5' />
              Editar
            </Button>
          )}

        {isPostTransmitResume && !isInspectionRejected && (
          <>
            <ModalGeneratePDF
              policyId={proposal?.policyId}
              productCode={proposal?.productCode}
              stage='proposal'
              disabled={proposalActionGuard.blocked}
              disabledReason={proposalActionGuard.reason}
            />

            <ModalSendMail
              emailProponent={proposal?.customer?.email || ''}
              policyId={proposal?.policyId}
              productCode={proposal?.productCode}
              disabled={proposalActionGuard.blocked}
              disabledReason={proposalActionGuard.reason}
            />
          </>
        )}

        {manualEmission.isVisible && (
          <Button
            variant='primary'
            size='md'
            className='flex w-full items-center justify-center gap-2 rounded-xl sm:w-auto'
            onClick={manualEmission.open}
            disabled={manualEmission.isPending}
          >
            {manualEmission.isPending ? (
              <>
                Emitindo...
                <Spinner size='sm' colorScheme='gray' />
              </>
            ) : (
              <>
                <FilePlus2 className='h-4 w-4' />
                Emitir manualmente
              </>
            )}
          </Button>
        )}

        {canTransmit && (
          <Button
            variant='primary'
            size='md'
            className='flex w-full items-center justify-center gap-2 rounded-xl sm:w-auto'
            onClick={() => setIsModalOpen(true)}
            disabled={isPending || proposalActionGuard.blocked}
            title={proposalActionGuard.reason}
          >
            {isPending ? (
              <>
                Processando...
                <Spinner size='sm' colorScheme='gray' />
              </>
            ) : (
              'Transmitir Proposta'
            )}
          </Button>
        )}

        <Link to='/proposals'>
          <Button
            variant='primary'
            size='md'
            className='flex w-full items-center justify-center gap-2 rounded-xl sm:w-auto'
            disabled={isPending}
          >
            Sair
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function ResumeGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className='flex min-w-0 flex-col items-start gap-4 self-stretch'>
      <Text className='text-sm font-bold text-alba-black-500'>{title}</Text>
      <div className='flex w-full min-w-0 flex-col items-start justify-center divide-y divide-alba-gray-200 rounded-xl border border-alba-gray-400 p-3 sm:p-4'>
        {children}
      </div>
    </div>
  );
}

const percentFormat: Intl.NumberFormatOptions = {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

function CommercializationResume({ agents }: { agents: ProposalAgent[] }) {
  const mainBroker = agents[0];
  const coBrokers = agents.slice(3);
  const hasCoBrokers = coBrokers.length > 0;

  const baseCommission = mainBroker?.shareRate
    ? mainBroker.commissionRate / mainBroker.shareRate
    : (mainBroker?.commissionRate ?? 0);

  return (
    <ResumeGroup title='Comercialização'>
      <ResumeItem label='Corretor' value={mainBroker?.name || ''} />
      <ResumeItem
        label='Percentual de Corretagem'
        value={baseCommission.toLocaleString('pt-BR', percentFormat)}
      />
      {hasCoBrokers && (
        <ResumeItem
          label='Participação'
          value={(mainBroker?.shareRate ?? 0).toLocaleString(
            'pt-BR',
            percentFormat
          )}
        />
      )}
      {coBrokers.map((coBroker, index) => (
        <div key={coBroker.pk ?? index} className='contents'>
          <ResumeItem
            className='mt-2 pt-3'
            label={`Co-Corretor${coBrokers.length > 1 ? ` ${index + 1}` : ''}`}
            value={coBroker.name || ''}
          />
          <ResumeItem
            label={`Participação Co-Corretor${coBrokers.length > 1 ? ` ${index + 1}` : ''}`}
            value={(coBroker.shareRate ?? 0).toLocaleString(
              'pt-BR',
              percentFormat
            )}
          />
        </div>
      ))}
    </ResumeGroup>
  );
}

export function ResumeItem({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-col items-start justify-between gap-1 self-stretch py-2 first:pt-0 last:pb-0 sm:flex-row sm:gap-2 ${className || ''}`}
    >
      <Text className='text-sm leading-4 font-bold text-alba-black-500 sm:flex-1'>
        {label}
      </Text>

      <Text className='break-words text-left text-sm leading-4 text-alba-gray-600 sm:text-right'>
        {value}
      </Text>
    </div>
  );
}

function DocumentationSection({
  policyId,
  inspectionId,
  isRejected,
  improvementLetterText,
  generatedAt,
}: {
  policyId: string;
  inspectionId: string;
  isRejected: boolean;
  improvementLetterText?: string;
  generatedAt?: string;
}) {
  const [downloadingRefusal, setDownloadingRefusal] = useState(false);
  const [downloadingImprovement, setDownloadingImprovement] = useState(false);
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);

  const { data: improvementAttachment } = useProposalInspectionDocument(
    policyId,
    'improvement_letter'
  );

  async function handleDownload(
    type: 'refusal_letter' | 'improvement_letter',
    setLoading: Dispatch<SetStateAction<boolean>>,
    fallbackName: string,
    notFoundDescription: string
  ) {
    setLoading(true);
    try {
      const response = await proposalsService.getInspectionDocument(
        policyId,
        type
      );

      if (!response.success || !response.data?.url) {
        toaster.create({
          type: 'error',
          title: 'Documento não encontrado',
          description: notFoundDescription,
        });
        return;
      }

      const link = document.createElement('a');
      link.href = response.data.url;
      link.download = response.data.key.split('/').pop() || fallbackName;
      link.target = '_blank';
      link.click();
    } catch {
      toaster.create({
        type: 'error',
        title: 'Erro ao baixar documento',
        description: 'Não foi possível baixar o documento. Tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className='flex flex-col items-start gap-4 self-stretch'>
        <Text className='text-sm font-bold text-alba-black-500'>
          Documentação
        </Text>
        <div className='flex flex-col items-start gap-3 p-4 self-stretch border border-alba-gray-400 rounded-xl'>
          {improvementLetterText && (
            <div className='flex flex-col gap-1 self-stretch'>
              <Text className='text-xs font-bold text-alba-black-500'>
                Carta de Melhorias
              </Text>
              <Text className='text-sm text-alba-black-400 whitespace-pre-wrap'>
                {improvementLetterText}
              </Text>
            </div>
          )}
          <div className='flex flex-row flex-wrap items-center gap-2'>
            {isRejected && (
              <Button
                variant='secondary'
                size='sm'
                disabled={downloadingRefusal}
                onClick={() =>
                  handleDownload(
                    'refusal_letter',
                    setDownloadingRefusal,
                    `carta-recusa-${policyId}.pdf`,
                    'A carta de recusa não está disponível para esta proposta.'
                  )
                }
              >
                <FileText className='w-4 h-4 mr-2' />
                {downloadingRefusal ? 'Baixando...' : 'Carta de Recusa'}
              </Button>
            )}
            {improvementAttachment && (
              <Button
                variant='secondary'
                size='sm'
                disabled={downloadingImprovement}
                onClick={() =>
                  handleDownload(
                    'improvement_letter',
                    setDownloadingImprovement,
                    `carta-melhoria-${policyId}`,
                    'A carta de melhoria não está disponível para esta proposta.'
                  )
                }
              >
                <TrendingUp className='w-4 h-4 mr-2' />
                {downloadingImprovement ? 'Baixando...' : 'Carta de Melhoria'}
              </Button>
            )}
            <Button
              variant='secondary'
              size='sm'
              disabled={!inspectionId}
              onClick={() => setIsAttachmentsModalOpen(true)}
            >
              <Folder className='w-4 h-4 mr-2' />
              Documentos da Inspeção
            </Button>
          </div>
          {generatedAt && (
            <Text className='text-xs text-alba-gray-600'>
              Gerado: {formatDateToBrazil(generatedAt)}
            </Text>
          )}
        </div>
      </div>

      <InspectionAttachmentsModal
        isOpen={isAttachmentsModalOpen}
        onClose={() => setIsAttachmentsModalOpen(false)}
        policyId={policyId}
        inspectionId={inspectionId}
      />
    </>
  );
}
